import {
  fetchPrimaryCareData,
  PrimaryCareApiResponse,
  searchPrimaryCareData,
} from "../../../api/bigQuery/pharmaPrimaryCareOrders";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { OrganizationType } from "../../../enums/organizationTypes";
import { logError } from "../../../utils/customLogger";
import { PrimaryCareFilterPayload } from "../hooks/useFilterPayload";
import { PrimaryCareTableState } from "../hooks/usePrimaryCareTableData";

/**
 * makePostCallForPrimaryCare - API Call for Filter/Search Changes
 *
 * Purpose: Fetches data when filters or search term changes.
 * Resets tableData to new results and increments pagination reset count.
 *
 * @param signal - AbortSignal for cancelling the request
 * @param payload - Filter payload containing search params
 * @param setPrimaryCareState - State setter for API response
 * @param setTableData - State setter for table data
 * @param setPaginationResetCount - Function to reset pagination
 */
export const makePostCallForPrimaryCare = async (
  signal: AbortSignal,
  payload: PrimaryCareFilterPayload,
  setPrimaryCareState: React.Dispatch<
    React.SetStateAction<PrimaryCareTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<AestraPrimaryCareTable[]>>,
  setPaginationResetCount: React.Dispatch<React.SetStateAction<number>>,
): Promise<void> => {
  try {
    setPrimaryCareState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    // const selectedOrganization =
    //   getItemFromLocalStorage(LocalStorageKeys.HEALTHCARE_FACILITY_ID) ?? "";

    const selectedOrganization = OrganizationType.ASTERA_PRIMARY_CARE;

    // Use search endpoint if keyword exists, otherwise use regular fetch
    const response: PrimaryCareApiResponse = payload.keyword
      ? await searchPrimaryCareData(
          {
            ...payload,
            org_id: selectedOrganization,
          },
          signal,
        )
      : await fetchPrimaryCareData(
          {
            ...payload,
            org_id: selectedOrganization,
          },
          signal,
        );

    if (response.details?.rows) {
      setPrimaryCareState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });
      setTableData(response.details.rows);

      // Reset pagination to page 1 when filters/search change
      setPaginationResetCount((prev) => prev + 1);
    } else {
      setPrimaryCareState((prev) => ({
        ...prev,
        loading: false,
        error: response.message || "Failed to fetch data",
      }));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setPrimaryCareState((prev) => ({
      ...prev,
      loading: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForPrimaryCare");
  }
};
